/**
 * Telephony problems (micro bloqué, navigateur incompatible, ligne non ouverte,
 * device en erreur), in two forms (T-d1, SR2-1):
 *
 * - TelephonyBanner: a non-blocking strip on every dialer page. The old
 *   full-screen modal covered Analytique, Paramètres or Facturation, where
 *   nobody needs a microphone.
 * - TelephonyBlocker: the modal, opened only when the user tries to start a
 *   session while calls cannot work (TELEPHONY_REQUIRED_EVENT, sent by the
 *   dial mutation on DEVICE_NOT_READY).
 *
 * Both show a translated message and a code (MIC_DENIED, MIC_UNSUPPORTED,
 * TOKEN_FAILED, TEL_OFFLINE; TEL_NOT_READY in the modal while the device is
 * still starting), never the raw server text. The CTA opens the
 * microphone and telephony check in the list's « Réglages d'appel ».
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MicOff, PhoneOff, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTwilioDevice } from '@/contexts/TwilioDeviceContext';
import { telephonyIssue, telephonyIssueOnStart, TELEPHONY_REQUIRED_EVENT, type TelephonyIssue } from '@/services/dialer/telephonyIssue';

// The microphone and call settings live in the list's « Réglages d'appel » (W4).
const SETTINGS_PATH = '/ParallelDialer?callSettings=open';

export function useTelephonyIssue(): TelephonyIssue | null {
  const { micPermissionStatus, deviceState, deviceErrorCode } = useTwilioDevice();
  return telephonyIssue({ micPermissionStatus, deviceState, deviceErrorCode });
}

function IssueIcon({ issue, className }: { issue: TelephonyIssue; className: string }) {
  if (issue.code === 'MIC_UNSUPPORTED') return <PhoneOff className={className} aria-hidden />;
  if (issue.code === 'MIC_DENIED') return <MicOff className={className} aria-hidden />;
  return <AlertTriangle className={className} aria-hidden />;
}

export function TelephonyBanner() {
  const { t } = useTranslation('dialer');
  const navigate = useNavigate();
  const { retryMicPermission } = useTwilioDevice();
  const issue = useTelephonyIssue();
  if (!issue) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="telephony-banner"
      className="mt-3 mb-1 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
    >
      <IssueIcon issue={issue} className="h-4 w-4 shrink-0" />
      <span className="font-medium">{t(issue.titleKey)}</span>
      <span className="text-amber-800">{t('dialer:telephonyBlocker.banner')}</span>
      <code className="rounded bg-amber-100 px-1.5 py-0.5 font-mono text-xs">{t('dialer:telephonyBlocker.codeLabel')} {issue.code}</code>
      <span className="ml-auto flex gap-2">
        <Button size="sm" variant="outline" className="h-7" onClick={retryMicPermission}>{t('dialer:telephonyBlocker.retry')}</Button>
        <Button size="sm" className="h-7" onClick={() => navigate(SETTINGS_PATH)}>{t('dialer:telephonyBlocker.checkTelephony')}</Button>
      </span>
    </div>
  );
}

export function TelephonyBlocker() {
  const { t } = useTranslation('dialer');
  const navigate = useNavigate();
  const { retryMicPermission, deviceReady } = useTwilioDevice();
  // A refused start always gets an answer, even while the device is still
  // starting and no blocking issue is known yet (TEL_NOT_READY).
  const issue = telephonyIssueOnStart(useTelephonyIssue());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onRequired = () => setOpen(true);
    window.addEventListener(TELEPHONY_REQUIRED_EVENT, onRequired);
    return () => window.removeEventListener(TELEPHONY_REQUIRED_EVENT, onRequired);
  }, []);

  // Telephony came back: nothing left to explain.
  useEffect(() => {
    if (deviceReady) setOpen(false);
  }, [deviceReady]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="telephony-blocker-title"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(2px)' }}
      onClick={() => setOpen(false)}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl border border-gray-200 max-w-md w-full p-7 space-y-5 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto">
          <IssueIcon issue={issue} className="w-7 h-7 text-red-600" />
        </div>
        <div>
          <h2 id="telephony-blocker-title" className="text-xl font-bold text-gray-900">{t(issue.titleKey)}</h2>
          <p className="mt-2 text-sm text-gray-600">{t(issue.descriptionKey)}</p>
          <p className="mt-2 font-mono text-xs text-gray-500">{t('dialer:telephonyBlocker.codeLabel')} {issue.code}</p>
        </div>
        <div className="flex flex-col gap-2 pt-1">
          <Button onClick={() => { setOpen(false); navigate(SETTINGS_PATH); }} className="w-full">
            {t('dialer:telephonyBlocker.checkTelephony')}
          </Button>
          <Button variant="outline" onClick={retryMicPermission} className="w-full">
            {t('dialer:telephonyBlocker.retry')}
          </Button>
        </div>
        <p className="text-xs text-gray-500">{t('dialer:telephonyBlocker.outboundDisabledHint')}</p>
      </div>
    </div>
  );
}

export default TelephonyBlocker;
